import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router'
import { Button, Card, ErrorNote } from '../components/ui'
import { ApiError } from '../lib/api'
import {
  AUDIT_ACTION_LABEL,
  describeChange,
  listAudit,
  type AuditAction,
  type AuditEventDto,
  type AuditFilters,
} from '../lib/audit-api'
import { formatJalaliDateTime, formatMobile, toPersianDigits } from '../lib/fa'
import { listStaff, type StaffDto } from '../lib/staff-api'

const messageOf = (error: unknown): string =>
  error instanceof ApiError ? error.messageFa : 'مشکلی پیش آمد. دوباره تلاش کنید.'

const selectClass =
  'h-10 rounded-xl border border-line bg-card px-3 outline-none focus:border-brand-500'

/** Who did what in the backoffice. Read-only, admins only — the API enforces both. */
export function AuditPage() {
  const [filters, setFilters] = useState<AuditFilters>({ page: 1 })
  const staff = useQuery({ queryKey: ['staff'], queryFn: listStaff })
  const audit = useQuery({
    queryKey: ['audit', filters],
    queryFn: () => listAudit(filters),
    placeholderData: keepPreviousData,
  })

  /** Any filter change starts over at the first page. */
  const filter = (patch: Partial<AuditFilters>) => setFilters((f) => ({ ...f, ...patch, page: 1 }))
  const staffById = new Map((staff.data ?? []).map((s) => [s.id, s]))
  const pages = audit.data ? Math.max(1, Math.ceil(audit.data.total / audit.data.pageSize)) : 1

  return (
    <div className="max-w-6xl">
      <h1 className="mb-1 text-2xl font-bold">رویدادها</h1>
      <p className="mb-6 text-sm text-muted">
        هر کاری که در پیشخوان انجام می‌شود اینجا ثبت می‌شود و قابل ویرایش یا حذف نیست.
      </p>

      <Card className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium">همکار</span>
          <select
            className={`${selectClass} w-48`}
            value={filters.staffId ?? ''}
            onChange={(e) => filter({ staffId: e.target.value || undefined })}
          >
            <option value="">همه</option>
            {(staff.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium">رویداد</span>
          <select
            className={`${selectClass} w-48`}
            value={filters.action ?? ''}
            onChange={(e) => filter({ action: (e.target.value || undefined) as AuditAction | undefined })}
          >
            <option value="">همه</option>
            {(Object.keys(AUDIT_ACTION_LABEL) as AuditAction[]).map((action) => (
              <option key={action} value={action}>
                {AUDIT_ACTION_LABEL[action]}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium">از</span>
          <input
            type="date"
            className={selectClass}
            value={filters.from ?? ''}
            onChange={(e) => filter({ from: e.target.value || undefined })}
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium">تا</span>
          <input
            type="date"
            className={selectClass}
            value={filters.to ?? ''}
            onChange={(e) => filter({ to: e.target.value || undefined })}
          />
        </label>
        <Button variant="secondary" onClick={() => setFilters({ page: 1 })}>
          پاک‌کردن فیلترها
        </Button>
      </Card>

      <Card className="mt-6 overflow-x-auto p-0">
        {audit.isPending ? (
          <p className="p-5 text-sm text-muted">در حال بارگذاری…</p>
        ) : audit.isError ? (
          <div className="p-5">
            <ErrorNote>{messageOf(audit.error)}</ErrorNote>
          </div>
        ) : audit.data.items.length === 0 ? (
          <p className="p-5 text-sm text-muted">رویدادی با این فیلترها پیدا نشد.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-line text-right text-muted">
              <tr>
                <th className="px-4 py-3 font-medium">زمان</th>
                <th className="px-4 py-3 font-medium">انجام‌دهنده</th>
                <th className="px-4 py-3 font-medium">رویداد</th>
                <th className="px-4 py-3 font-medium">روی</th>
                <th className="px-4 py-3 font-medium">تغییر</th>
                <th className="px-4 py-3 font-medium">IP</th>
              </tr>
            </thead>
            <tbody>
              {audit.data.items.map((event) => (
                <AuditRow key={event.id} event={event} target={staffById.get(event.targetId)} />
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {audit.data && audit.data.total > 0 ? (
        <div className="mt-4 flex items-center gap-3 text-sm">
          <Button
            variant="secondary"
            disabled={filters.page <= 1 || audit.isFetching}
            onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
          >
            قبلی
          </Button>
          <span className="text-muted">
            صفحه {toPersianDigits(filters.page)} از {toPersianDigits(pages)} — {toPersianDigits(audit.data.total)} رویداد
          </span>
          <Button
            variant="secondary"
            disabled={filters.page >= pages || audit.isFetching}
            onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
          >
            بعدی
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function AuditRow({ event, target }: { event: AuditEventDto; target?: StaffDto }) {
  const label = AUDIT_ACTION_LABEL[event.action as AuditAction] ?? event.action
  const change = describeChange(event)

  return (
    <tr className="border-b border-line align-top last:border-0">
      <td className="whitespace-nowrap px-4 py-3 text-muted">{formatJalaliDateTime(event.createdAt)}</td>
      <td className="px-4 py-3 font-medium">{event.staff?.name ?? <span className="text-muted">خط فرمان</span>}</td>
      <td className="px-4 py-3">{label}</td>
      <td className="px-4 py-3">
        {target ? (
          <>
            {target.name}
            <div className="text-xs text-muted" dir="ltr" style={{ textAlign: 'right' }}>
              {formatMobile(target.mobile)}
            </div>
          </>
        ) : event.targetType === 'User' ? (
          <Link to={`/customers/${event.targetId}`} className="font-medium hover:text-brand-600">
            مشتری
            <div className="text-xs text-muted" dir="ltr" style={{ textAlign: 'right' }}>
              {event.targetId}
            </div>
          </Link>
        ) : (
          <span className="text-xs text-muted" dir="ltr">
            {event.targetType} {event.targetId}
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        {change.length > 0 ? change.map((line) => <div key={line}>{line}</div>) : <span className="text-muted">—</span>}
      </td>
      <td className="px-4 py-3 text-muted" dir="ltr" style={{ textAlign: 'right' }} title={event.userAgent ?? undefined}>
        {event.ip ?? '—'}
      </td>
    </tr>
  )
}
